'use client';

import { useEffect, useRef, useState } from 'react';
import { loraApi } from '@/api/connectivity/lora';
import { useTechnology } from '@/hooks/technology';
import { useToast } from '@/hooks/use-toast';
import type { ISerialPort } from '@/hooks/useSerialPort';
import { fetchWithAuth, getUserFromToken } from '@/lib/api';
import { sleep } from '@/lib/utils';
import type { Device } from '@/types/production/device';
import type {
  IIdentificationLog,
  IIdentificationLogLoraKeys,
} from '@/types/production/identification-log';
import type { ITechnology } from '@/types/production/tool';

namespace Namespace {
  export interface UseIdentificationProps {
    technology: ITechnology | null;
  }

  export interface Detected {
    port: ISerialPort;
    equipment?: Equipment | undefined;
    status: 'fully_identified' | 'partially_identified' | 'not_identified';
  }

  export interface CurrentEquipment extends Omit<Equipment, 'firmware' | 'serial'> {
    serial: string;
    firmware: string;
  }

  export interface CurrentDetected extends Omit<Detected, 'equipment'> {
    equipment: CurrentEquipment;
  }

  interface Equipment {
    firmware?: string;
    serial?: string;
    imei?: string | undefined;
    iccid?: string | undefined;
    /** MAC address BLE — apenas BWS Buscador. */
    mac?: string | undefined;
    lora_keys?: Partial<Device['Equipment']['lora_keys']>;
  }

  export interface Identification extends IIdentificationLog {}

  export interface IdentificationResult {
    ok: true;
    port: ISerialPort;
    messages: any[];
    init_time: number;
    end_time: number;
    status: boolean;
    equipment_before: {
      serial: string;
      imei?: string;
      /** MAC address BLE — apenas BWS Buscador. */
      mac?: string;
      lora_keys: Device['Equipment']['lora_keys'];
    };
    equipment_after: {
      serial: string;
      imei?: string;
      /** MAC address BLE — apenas BWS Buscador. */
      mac?: string;
      lora_keys: Device['Equipment']['lora_keys'];
    };
  }
}

// Tecnologias cuja identificação consulta serial-relations (serial ↔ IMEI) e grava
// o IMEI cadastrado no dispositivo (WIMEI) antes do serial.
const SERIAL_RELATION_TECHNOLOGIES = [
  'DM_BWS_NB2',
  'DM_BWS_NB2_LORA',
  'DM_BWS_LL_NB',
  'DM_BWS_LL_NB_LORA',
  'DM_BWS_BAIT_NB',
  'DM_BWS_BAIT_NB_LORA',
];

// Tempo máximo de espera por uma detecção em andamento antes de identificar.
const DETECTION_WAIT_TIMEOUT_MS = 15000;

// Technologies whose devices must be registered in KORA após a gravação de serial.
// As chaves LoRa e o timestamp são gravados no dispositivo durante a identificação,
// então o cadastro na KORA acontece aqui (e não na configuração). O cadastro não
// ativa o dispositivo: a ativação é feita depois, na página de Ativação LoRa.
const KORA_REGISTRABLE_TECHNOLOGIES = [
  'DM_BWS_NB2_LORA',
  'DM_BWS_LL_NB_LORA',
  'DM_BWS_BAIT_NB_LORA',
];

type KoraRegistrationState = 'started' | 'skipped' | 'failed';

// Helper function to transform lora_keys from frontend format to backend format.
// O schema do log só aceita os nomes curtos; os longos seriam descartados.
function transformLoraKeys(
  lora_keys?: Partial<Device['Equipment']['lora_keys']>,
): IIdentificationLogLoraKeys | undefined {
  if (!lora_keys) return undefined;

  return {
    dev_eui: lora_keys.device_eui,
    app_eui: lora_keys.application_eui,
    app_key: lora_keys.application_key,
    dev_addr: lora_keys.device_address,
    nwk_s_key: lora_keys.network_session_key,
    app_s_key: lora_keys.application_session_key,
  };
}

export const useIdentification = (props: Namespace.UseIdentificationProps) => {
  const { technology } = props;
  const [detected, setDetected] = useState<Namespace.Detected[]>([]);
  const [isDetecting, setIsDetecting] = useState(false);
  // Use ref to prevent concurrent detections (synchronous check)
  const isDetectingRef = useRef(false);

  const [identified, setIdentified] = useState<Namespace.Identification[]>([]);
  const [isIdentifying, setIsIdentifying] = useState(false);
  // Use ref to prevent detection during identification (synchronous check)
  const isIdentifyingRef = useRef(false);

  const { toast } = useToast();

  // hook that handle interactions with devices
  const { ports, handleDetection, handleIdentification, requestPort, isIdentified } =
    useTechnology(technology);

  // Helper function to fetch the registered relation (IMEI and, for the BWS
  // Buscador, the BLE MAC address) from serial-relations.
  const fetchRelationFromSerialRelations = async (
    serial: string,
  ): Promise<{ imei?: string; mac?: string } | undefined> => {
    try {
      console.log('[SERIAL-RELATIONS] Fetching relation for serial:', serial);
      const response = await fetchWithAuth(`/api/production/devices/serial-relations/${serial}`);
      console.log('[SERIAL-RELATIONS] Response status:', response.status, 'ok:', response.ok);

      if (response.ok) {
        // A API responde no envelope { success, statusCode, data: { imei, serial, mac } }.
        // O IMEI/serial/mac da relação estão em `data`; fallback para o corpo direto.
        const body = await response.json();
        const relation = body?.data ?? body;
        console.log(
          '[SERIAL-RELATIONS] imei:',
          relation?.imei,
          'mac:',
          relation?.mac,
          'for serial:',
          relation?.serial,
        );
        return { imei: relation?.imei, mac: relation?.mac };
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('[SERIAL-RELATIONS] Error response:', errorData);
      }
      return undefined;
    } catch (error) {
      console.error('[SERIAL-RELATIONS] Error fetching relation:', error);
      return undefined;
    }
  };

  // Inicia o cadastro do dispositivo na KORA via /lora-keys/create usando as
  // chaves LoRa lidas do próprio dispositivo após a gravação de serial. O job é
  // assíncrono (Pulsar) — aqui apenas disparamos e reportamos se foi aceito.
  const registerDeviceInKora = async (
    serial: string,
    lora_keys: Partial<Device['Equipment']['lora_keys']> | undefined,
    user: { id: string; name: string },
  ): Promise<KoraRegistrationState> => {
    const device_eui = lora_keys?.device_eui;

    if (!device_eui) {
      console.warn(
        `[KORA] Cadastro pulado para ${serial || 'dispositivo sem serial'}: device EUI ausente`,
      );
      return 'skipped';
    }

    try {
      await loraApi.createDevice({
        device_eui,
        serial,
        device_address: lora_keys?.device_address ?? '',
        app_eui: lora_keys?.application_eui ?? '',
        app_key: lora_keys?.application_key ?? '',
        app_session_key: lora_keys?.application_session_key ?? '',
        network_key: lora_keys?.network_session_key ?? '',
        author: { user_uuid: user.id, name: user.name },
      });
      return 'started';
    } catch (error) {
      console.error('[KORA] Falha ao iniciar cadastro do dispositivo', { serial }, error);
      return 'failed';
    }
  };

  // function that handle the identification process, check if the process was successful and save result on database
  const identify = async (id: string, detected: Namespace.Detected[], technology: ITechnology) => {
    try {
      isIdentifyingRef.current = true;
      setIsIdentifying(true);
      const [current] = detected;

      console.log(
        '[IDENTIFY] Starting identification. Technology:',
        technology?.name.system,
        'Serial ID:',
        id,
      );

      if (!current.equipment || !technology) {
        toast({
          variant: 'destructive',
          description: 'Nenhum equipamento está apto para ser identificado.',
          title: 'Erro',
        });
        return;
      }

      const isBuscador = technology.name.system === 'DM_BWS_BUSCADOR';
      const usesSerialRelations =
        SERIAL_RELATION_TECHNOLOGIES.includes(technology.name.system) || isBuscador;

      // NB2, NB2 LoRa, BWS LL NB, BWS LL NB+LoRa, BWS BAIT NB, BWS BAIT NB+LoRa e Buscador buscam
      // os dados de fábrica no cadastro (serial-relations) ANTES de gravar. O Buscador precisa
      // também do MAC BLE.
      let registeredImei: string | undefined;
      let registeredMac: string | undefined;

      if (usesSerialRelations) {
        console.log('[IDENTIFY] Fetching relation from serial-relations...');
        const relation = await fetchRelationFromSerialRelations(id);
        registeredImei = relation?.imei;
        registeredMac = relation?.mac;

        // O MAC só existe no cadastro do Buscador e não pode ser derivado da
        // etiqueta. Sem ele o dispositivo ficaria pela metade, então a gravação
        // é abortada antes de qualquer comando ser enviado — nenhum log é criado.
        if (isBuscador && !registeredMac) {
          console.error('[IDENTIFY] MAC ausente no cadastro para o serial:', id);
          toast({
            variant: 'destructive',
            title: 'MAC não cadastrado',
            description: `O serial ${id} não possui MAC address cadastrado. A gravação foi cancelada.`,
          });
          return;
        }
      } else {
        console.log('[IDENTIFY] Modelo sem lookup em serial-relations');
      }

      // Uma detecção em andamento usa a mesma porta e o mesmo reader: se a
      // identificação começar junto, as respostas se dividem entre os dois leitores
      // e a detecção fecha a porta no meio do processo. Espera ela terminar.
      if (isDetectingRef.current) {
        console.info('[IDENTIFY] Detecção em andamento, aguardando terminar...');
        const waitStart = Date.now();
        while (isDetectingRef.current && Date.now() - waitStart < DETECTION_WAIT_TIMEOUT_MS) {
          await sleep(100);
        }
        console.info(`[IDENTIFY] Detecção liberou a porta após ${Date.now() - waitStart}ms`);
      }

      // run the process - pass registered IMEI (and MAC, for the Buscador) to be written
      const result = (await handleIdentification(
        current as Namespace.CurrentDetected,
        id,
        registeredImei, // Pass IMEI to be written to device
        registeredMac, // Pass MAC to be written to device (Buscador only)
      )) as Namespace.IdentificationResult;

      const user = getUserFromToken();
      if (!user) {
        toast({
          variant: 'destructive',
          description: 'Usuário não autenticado.',
          title: 'Erro',
        });
        return;
      }

      // For NB2, use registered IMEI from serial-relations instead of detected IMEI (only for device writing, not DB)
      const finalImei =
        usesSerialRelations && registeredImei ? registeredImei : result.equipment_after.imei;

      const equipmentAfter = {
        ...result.equipment_after,
        imei: finalImei,
        // MAC: registra o valor CONFIRMADO pelo eco do dispositivo (vindo do
        // driver), não o esperado do cadastro. Se o eco divergir ou não chegar,
        // o campo fica vazio e o status já é falso — o log nunca afirma uma
        // gravação que o dispositivo não confirmou.
      };

      console.log('[IDENTIFY] Equipment after identification:', equipmentAfter);
      console.log('[IDENTIFY] Equipment before identification:', result.equipment_before);
      console.log('[IDENTIFY] Final IMEI (written to device):', finalImei);

      // O ICCID é do SIM card e não muda na identificação: vem da detecção, que o lê
      // do dispositivo. Fica vazio nos modelos sem SIM (ex.: LoRa).
      const iccid = current.equipment.iccid;

      const log: Omit<IIdentificationLog, 'id' | 'created_at'> = {
        equipment_before: {
          ...result.equipment_before,
          iccid,
          lora_keys: transformLoraKeys(result.equipment_before.lora_keys),
        },
        equipment_after: {
          ...equipmentAfter,
          iccid,
          lora_keys: transformLoraKeys(equipmentAfter.lora_keys),
        },
        messages: result.messages,
        status: result.status,
        end_time: result.end_time,
        init_time: result.init_time,
        technology: {
          id: technology.id,
          system_name: technology.name.system,
        },
        user,
      };

      // save identification log to database
      const logResponse = await fetchWithAuth('/api/production/identification-logs', {
        method: 'POST',
        body: JSON.stringify(log),
      });

      if (!logResponse.ok) {
        const errorBody = await logResponse.text().catch(() => '<sem corpo>');
        console.error('[IDENTIFY] Falha ao salvar log:', {
          status: logResponse.status,
          body: errorBody,
          payload: log,
        });
        throw new Error(
          `Erro ao salvar log de identificação (${logResponse.status}): ${errorBody}`,
        );
      }
      // A API responde no envelope { success, statusCode, data }. O log salvo
      // (com equipment_before/after, status e created_at) está em `data`;
      // usamos um fallback para o próprio corpo caso a rota retorne o log direto.
      const logBody = await logResponse.json();
      const dataSavedOnDb = logBody?.data ?? logBody;

      // save device to database (for all models including NB2)
      let koraState: KoraRegistrationState | undefined;
      if (result.status) {
        // For NB2, use the registered IMEI (the one written to device)
        const equipmentPayload = {
          ...(current.equipment as Namespace.CurrentEquipment),
          ...equipmentAfter,
        };

        console.log('[IDENTIFY] Saving device to DB:', equipmentPayload);

        const deviceResponse = await fetchWithAuth('/api/production/devices/upsert', {
          method: 'POST',
          body: JSON.stringify({
            filter: {
              'equipment.serial': result.equipment_after.serial,
            },
            payload: {
              equipment: equipmentPayload,
              simcard: current.equipment?.iccid ? { iccid: current.equipment?.iccid } : undefined,
              model: technology.name.system as any,
              identified_at: new Date(),
            },
          }),
        });

        // O cadastro na KORA só pode acontecer com o device já salvo.
        if (!deviceResponse.ok) {
          const errorBody = await deviceResponse.text().catch(() => '<sem corpo>');
          console.error('[IDENTIFY] Falha ao salvar device:', {
            status: deviceResponse.status,
            body: errorBody,
          });
          throw new Error(`Erro ao salvar o dispositivo (${deviceResponse.status}): ${errorBody}`);
        }

        // Cadastro na KORA (NB2 LoRa): o serial, o timestamp e as chaves LoRa
        // acabaram de ser gravados no dispositivo, então iniciamos o cadastro aqui.
        if (KORA_REGISTRABLE_TECHNOLOGIES.includes(technology.name.system)) {
          koraState = await registerDeviceInKora(
            result.equipment_after.serial,
            result.equipment_after.lora_keys,
            user,
          );
        }
      }

      // update react state with the result
      setIdentified((prev) => prev.concat(dataSavedOnDb));

      // O processo pode terminar sem erro e ainda assim reprovar (serial, timestamp
      // ou chaves não conferem): nesse caso o device não é salvo.
      if (!result.status) {
        toast({
          variant: 'destructive',
          title: 'Erro',
          description: 'Falha na identificação: os dados lidos do dispositivo não conferem.',
        });
      } else if (koraState === 'failed') {
        toast({
          variant: 'warning',
          title: 'Atenção!',
          description: 'Serial gravado, mas falhou ao iniciar o cadastro na KORA.',
        });
      } else if (koraState === 'skipped') {
        toast({
          variant: 'warning',
          title: 'Atenção!',
          description: 'Serial gravado, mas o cadastro na KORA foi pulado (sem device EUI).',
        });
      } else {
        toast({
          variant: 'success',
          title: 'Sucesso',
          description:
            koraState === 'started'
              ? 'Identificação concluída. Cadastro do dispositivo na KORA iniciado.'
              : 'Identificação concluída com sucesso.',
        });
      }
    } catch (_e) {
      toast({
        variant: 'destructive',
        title: 'error',
        description: 'Ocorreu algum erro durante a o processo de identificação.',
      });
    } finally {
      // indicate that the process end
      isIdentifyingRef.current = false;
      setIsIdentifying(false);
    }
  };

  // useEffect used to identify devices when connected via serial ports
  // Same pattern as useConfiguration - using setInterval every 5 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      if (isIdentifyingRef.current) return;

      // Use ref for synchronous check - prevents concurrent detections
      if (!isDetectingRef.current && ports.length) {
        isDetectingRef.current = true;
        setIsDetecting(true);
        try {
          const detectedResult = await handleDetection(ports);

          setDetected(() => {
            const map = new Map();

            for (const { port, response } of detectedResult) {
              // Use port as key to avoid grouping devices without serial
              map.set(port, {
                port,
                equipment: response,
                status: !response ? 'not_identified' : isIdentified(response),
              });
            }

            return Array.from(map.values());
          });
        } finally {
          isDetectingRef.current = false;
          setIsDetecting(false);
        }
      } else if (!isDetectingRef.current && !ports.length) {
        setDetected([]);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [ports, handleDetection, isIdentified]);

  return {
    detected,
    identified,
    identify,
    requestPort,
    isIdentifying,
    isDetecting,
  };
};

export default useIdentification;
